import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('調剤');
  const [showRokujouModal, setShowRokujouModal] = useState(false);
  const [showReishiModal, setShowReishiModal] = useState(false);
  const [visitorCount, setVisitorCount] = useState(0);

  useEffect(() => {
    // Get visitor count from localStorage
    const storedCount = localStorage.getItem('isshinDoVisitorCount');
    let currentCount = storedCount ? parseInt(storedCount, 10) : 1000000;
    
    // Increment count
    currentCount += 1;
    
    // Save to localStorage
    localStorage.setItem('isshinDoVisitorCount', currentCount.toString());
    
    // Update state
    setVisitorCount(currentCount);
  }, []);

  const services = [
    {
      title: '調剤',
      icon: 'ri-medicine-bottle-line',
      description: '処方せんに基づく正確な調剤を行います',
      image: 'https://public.readdy.ai/ai/img_res/4f891c32cd3b6f3603aac43b3f926547.jpg',
      link: '/dispensing'
    },
    {
      title: 'OTC医薬品',
      icon: 'ri-capsule-line',
      description: '一般医薬品の販売と適切なアドバイス',
      image: 'https://readdy.ai/api/search-image?query=Over-the-counter%20medications%20display%20in%20pharmacy%2C%20organized%20medicine%20shelves%2C%20clean%20white%20background%2C%20professional%20healthcare%20setting%2C%20bright%20lighting%2C%20realistic%20photography&width=300&height=200&seq=2&orientation=landscape',
      link: '/otc'
    },
    {
      title: '漢方相談',
      icon: 'ri-leaf-line',
      description: '体質に合わせた漢方薬の調合',
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20herbal%20medicine%20ingredients%2C%20dried%20herbs%20in%20wooden%20bowls%2C%20natural%20lighting%2C%20clean%20white%20background%2C%20traditional%20pharmacy%20setting%2C%20realistic%20photography&width=300&height=200&seq=3&orientation=landscape',
      link: '/kampo'
    },
    {
      title: '健康相談',
      icon: 'ri-heart-pulse-line',
      description: '疲労・慢性疾患・生活習慣病の相談',
      image: 'https://readdy.ai/api/search-image?query=Friendly%20pharmacist%20consulting%20with%20patient%2C%20healthcare%20consultation%2C%20warm%20lighting%2C%20professional%20medical%20environment%2C%20clean%20white%20background%2C%20realistic%20photography&width=300&height=200&seq=4&orientation=landscape',
      link: null
    },
    {
      title: '在宅訪問',
      icon: 'ri-home-heart-line',
      description: '在宅医療・服薬管理サポート',
      image: 'https://readdy.ai/api/search-image?query=Home%20healthcare%20visit%2C%20pharmacist%20with%20medical%20bag%20visiting%20patient%20at%20home%2C%20warm%20lighting%2C%20professional%20healthcare%20service%2C%20realistic%20photography&width=300&height=200&seq=5&orientation=landscape',
      link: null
    }
  ];

  const symptoms = [
    { name: '疲労・だるさ', color: 'bg-green-100 text-green-800' },
    { name: '尋常性白斑', color: 'bg-blue-100 text-blue-800' },
    { name: 'アトピー性皮膚炎', color: 'bg-purple-100 text-purple-800' },
    { name: '円形脱毛症', color: 'bg-indigo-100 text-indigo-800' },
    { name: '尋常性乾癬', color: 'bg-yellow-100 text-yellow-800' },
    { name: '掌蹠膿疱症', color: 'bg-pink-100 text-pink-800' }
  ];

  const scrollToMap = () => {
    const mapSection = document.getElementById('map-section');
    if (mapSection) {
      mapSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handlePageNavigation = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Pharmacy",
    "name": "一心堂薬局",
    "description": "千葉県香取郡多古町の漢方・健康相談・在宅支援薬局",
    "url": `${import.meta.env.VITE_SITE_URL}`,
    "telephone": "0479-70-6065",
    "faxNumber": "0479-70-6064",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "多古1038-1",
      "addressLocality": "多古町",
      "addressRegion": "千葉県香取郡",
      "postalCode": "289-2241",
      "addressCountry": "JP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "35.7456",
      "longitude": "140.4756"
    },
    "openingHours": "Mo-Fr 09:00-18:00, Sa 09:00-17:00",
    "priceRange": "$$",
    "paymentAccepted": ["Cash", "Credit Card"],
    "currenciesAccepted": "JPY",
    "founder": {
      "@type": "Person",
      "name": "佐藤勝利",
      "jobTitle": "代表薬剤師"
    },
    "serviceArea": {
      "@type": "Place",
      "name": "千葉県香取郡多古町"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "薬局サービス",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "調剤",
            "description": "処方せんに基づく正確な調剤"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "漢方相談",
            "description": "体質に合わせた漢方薬の調合"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "健康相談",
            "description": "疲労・慢性疾患・生活習慣病の相談"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "在宅訪問",
            "description": "在宅医療・服薬管理サポート"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <SEOHead
        title="一心堂薬局 - 香取郡多古町の漢方・健康相談・在宅支援薬局"
        description="千葉県香取郡多古町の一心堂薬局。漢方相談、健康相談、在宅訪問、調剤など体に優しい薬と自然に寄り添う治療を提供。代表薬剤師：佐藤勝利"
        keywords="多古町薬局,香取郡漢方薬,在宅薬局,一心堂薬局,漢方相談,健康相談,調剤薬局,千葉県薬局"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/`}
        ogTitle="一心堂薬局 - 香取郡多古町の漢方・健康相談・在宅支援薬局"
        ogDescription="千葉県香取郡多古町の一心堂薬局。漢方相談、健康相談、在宅訪問、調剤など体に優しい薬と自然に寄り添う治療を提供。"
        ogType="website"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
            </div>
            <button className="w-8 h-8 flex items-center justify-center">
              <i className="ri-menu-line text-green-800 text-xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-green-800 mb-2">
            千葉県香取郡多古町の<br />頼れる薬局
          </h1>
          <p className="text-green-600 text-lg mb-4">
            漢方・健康相談・在宅支援
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            体に優しい薬と自然に寄り添う治療で<br />
            ひとりひとりの最適なケアを提供します
          </p>
          <div className="flex space-x-3">
            <a 
              href="tel:0479-70-6065"
              className="flex-1 bg-green-600 text-white py-3 px-4 rounded-full font-medium text-center"
            >
              <i className="ri-phone-line mr-2"></i>
              お電話
            </a>
            <button 
              onClick={scrollToMap}
              className="flex-1 bg-white text-green-600 py-3 px-4 rounded-full font-medium border border-green-200"
            >
              <i className="ri-map-pin-line mr-2"></i>
              アクセス
            </button>
          </div>
        </div>
      </div>

      {/* Interior Photos Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            店内のご紹介
          </h2>
          <div className="space-y-4">
            <div className="bg-green-50 rounded-2xl p-4">
              <img 
                src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/3cc8f7ce9faf10f9885935c55482253d.jpeg"
                alt="調剤室"
                className="w-full h-40 object-cover rounded-xl mb-3"
              />
              <h3 className="font-semibold text-green-800 mb-1">調剤室</h3>
              <p className="text-sm text-gray-600">清潔で安全な環境で正確な調剤を行います</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-2xl p-4">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/7b53e066f55b0bb97fa0a48558ece5d3.jpeg"
                  alt="相談コーナー"
                  className="w-full h-24 object-cover rounded-lg mb-2"
                />
                <h3 className="font-semibold text-green-800 text-sm mb-1">相談コーナー</h3>
                <p className="text-xs text-gray-600">プライベートな空間でゆっくりご相談</p>
              </div>
              <div className="bg-green-50 rounded-2xl p-4">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/862982a5a6e5d78d2d2486c63736bb07.jpeg"
                  alt="漢方薬棚"
                  className="w-full h-24 object-cover rounded-lg mb-2"
                />
                <h3 className="font-semibold text-green-800 text-sm mb-1">漢方薬棚</h3>
                <p className="text-xs text-gray-600">豊富な種類の漢方薬を取り揃え</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Haru Character Section */}
      <div className="px-4 py-8 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-4 flex justify-center space-x-4">
            <img 
              src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/3460c9da23d5d2e9020d57071abda6fe.png"
              alt="一心堂キャラクター ハルちゃん"
              className="w-40 h-40 object-contain"
            />
            <img 
              src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/0ab9099937a17344a6b018e194885a8f.png"
              alt="一心堂キャラクター"
              className="w-40 h-40 object-contain"
            />
          </div>
          <h3 className="text-lg font-bold text-green-800 mb-2">
            一心堂キャラクター「ハルちゃん」
          </h3>
          <p className="text-sm text-gray-600">
            皆さまの健康をサポートします
          </p>
        </div>
      </div>

      {/* Video Section */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            商品紹介動画
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <a 
              href="https://youtube.com/shorts/NdReHo4kyO0"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/3eca43f445964b2c157d4f1e389c46eb.png"
                  alt="朝鮮人参物語〜1"
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-green-600 text-2xl ml-1"></i>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-green-800 text-sm mb-1">朝鮮人参物語〜1</h3>
                <p className="text-xs text-gray-600">タッチして動画を見る</p>
              </div>
            </a>

            <a 
              href="https://youtube.com/shorts/bDFnSkfw5HQ"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/93e1bc32994ca143b1c1015ce2016f8b.png"
                  alt="朝鮮人参物語〜2"
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-green-600 text-2xl ml-1"></i>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-green-800 text-sm mb-1">朝鮮人参物語〜2</h3>
                <p className="text-xs text-gray-600">タッチして動画を見る</p>
              </div>
            </a>

            <a 
              href="https://youtube.com/shorts/3r9KSpyE0qk"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/b011ba075091b538dfeae53612920f6d.png"
                  alt="朝鮮人参物語〜3"
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                    <i className="ri-play-fill text-green-600 text-2xl ml-1"></i>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-green-800 text-sm mb-1">朝鮮人参物語〜3</h3>
                <p className="text-xs text-gray-600">タッチして動画を見る</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            業務内容
          </h2>
          <div className="space-y-3">
            <Link to="/dispensing" onClick={handlePageNavigation} className="block">
              <div className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Japanese%20pharmacist%20preparing%20prescription%20medicine%2C%20professional%20pharmacy%20setting%2C%20clean%20white%20background%2C%20realistic%20photography&width=300&height=200&seq=1&orientation=landscape"
                      alt="調剤業務"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800 mb-1">調剤業務</h3>
                    <p className="text-sm text-gray-600">処方箋に基づく調剤・服薬指導</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-green-600 text-xl"></i>
                </div>
              </div>
            </Link>

            <Link to="/otc" onClick={handlePageNavigation} className="block">
              <div className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Over-the-counter%20medicine%20bottles%20and%20packages%2C%20pharmacy%20shelf%2C%20clean%20white%20background%2C%20realistic%20product%20photography&width=300&height=200&seq=2&orientation=landscape"
                      alt="OTC医薬品"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800 mb-1">OTC医薬品</h3>
                    <p className="text-sm text-gray-600">一般用医薬品の販売・相談</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-green-600 text-xl"></i>
                </div>
              </div>
            </Link>

            <Link to="/kampo" onClick={handlePageNavigation} className="block">
              <div className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Traditional%20Japanese%20herbal%20medicine%20kampo%2C%20natural%20herbs%20and%20roots%2C%20wooden%20background%2C%20realistic%20photography&width=300&height=200&seq=3&orientation=landscape"
                      alt="漢方相談"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800 mb-1">漢方相談</h3>
                    <p className="text-sm text-gray-600">体質に合わせた漢方薬のご提案</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-green-600 text-xl"></i>
                </div>
              </div>
            </Link>

            <Link to="/health-consultation" onClick={handlePageNavigation} className="block">
              <div className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Friendly%20pharmacist%20consulting%20with%20patient%2C%20healthcare%20consultation%20warm%20lighting%2C%20professional%20medical%20environment%2C%20clean%20white%20background%2C%20realistic%20photography&width=300&height=200&seq=4&orientation=landscape"
                      alt="健康相談"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800 mb-1">健康相談</h3>
                    <p className="text-sm text-gray-600">疲労・慢性疾患・生活習慣病の相談</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-green-600 text-xl"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Symptoms Section */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-4 text-center">
            よくあるご相談
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6">
            このような症状でお悩みの方、お気軽にご相談ください
          </p>
          <div className="grid grid-cols-2 gap-3">
            {symptoms.map((symptom, index) => (
              <div key={index}>
                {symptom.name === '疲労・だるさ' ? (
                  <Link 
                    to="/fatigue"
                    onClick={handlePageNavigation}
                    className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium block hover:opacity-80 transition-opacity`}
                  >
                    {symptom.name}
                  </Link>
                ) : symptom.name === '尋常性白斑' ? (
                  <Link 
                    to="/vitiligo"
                    onClick={handlePageNavigation}
                    className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium block hover:opacity-80 transition-opacity`}
                  >
                    {symptom.name}
                  </Link>
                ) : symptom.name === '円形脱毛症' ? (
                  <Link 
                    to="/alopecia"
                    onClick={handlePageNavigation}
                    className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium block hover:opacity-80 transition-opacity`}
                  >
                    {symptom.name}
                  </Link>
                ) : symptom.name === '尋常性乾癬' ? (
                  <Link 
                    to="/psoriasis"
                    onClick={handlePageNavigation}
                    className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium block hover:opacity-80 transition-opacity`}
                  >
                    {symptom.name}
                  </Link>
                ) : symptom.name === '掌蹠膿疱症' ? (
                  <Link 
                    to="/palmoplantar-pustulosis"
                    onClick={handlePageNavigation}
                    className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium block hover:opacity-80 transition-opacity`}
                  >
                    {symptom.name}
                  </Link>
                ) : (
                  <div className={`${symptom.color} py-3 px-4 rounded-xl text-center text-sm font-medium`}>
                    {symptom.name}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <button className="bg-green-600 text-white py-3 px-8 rounded-full font-medium">
              健康相談について詳しく
            </button>
          </div>
        </div>
      </div>

      {/* Recommended Herbs Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-4 text-center">
            おすすめの生薬
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6">
            体質改善に効果的な厳選された生薬をご紹介します
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">牛黄</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/27f441abda116cbe850d6a380a98ba68.png"
                          alt="牛黄"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>牛黄は牛の胆嚢にできる結石で、非常に貴重な動物性生薬です。古くから「心を開き、熱を冷まし、毒を解く」薬として珍重されてきました。</p>
                        <p>強心作用、解熱作用、鎮静作用があり、高熱や意識障害、けいれんなどの緊急時に用いられます。また、肝臓の働きを助け、解毒作用も持っています。</p>
                        <p>現代では救心や六神丸などの漢方薬に配合され、心臓の動悸や息切れ、ストレスによる不調にも使われています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open 牛黄 modal:', err);
                }
              }}
              className="bg-amber-100 text-amber-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-amber-200 transition-colors"
            >
              牛黄
            </button>

            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">田七人参</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/4c12d0054d8f3857bab8bc4daf588d1c.png"
                          alt="田七人参"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>田七人参は、中国雲南省の高地で育つウコギ科の植物で、「金不換」と称されるほど貴重な薬草です。</p>
                        <p>血を整える働きがあり、「止血と活血」を併せ持つ点が大きな特徴です。つまり、出血を抑えつつ血の巡りを良くするという相反する作用を兼ね備えています。</p>
                        <p>打撲や内出血、冷えや肩こり、月経痛など血流に関係する不調に幅広く用いられます。また、血管をしなやかに保ち、生活習慣病の予防にも役立つとされています。</p>
                        <p>現代ではサプリメントとしても人気が高く、健康維持や体質改善を目的に活用されています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open 田七人参 modal:', err);
                }
              }}
              className="bg-green-100 text-green-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-green-200 transition-colors"
            >
              田七人参
            </button>

            <button 
              onClick={() => setShowReishiModal(true)}
              className="bg-purple-100 text-purple-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-purple-200 transition-colors"
            >
              霊芝
            </button>

            <button 
              onClick={() => setShowRokujouModal(true)}
              className="bg-teal-100 text-teal-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-teal-200 transition-colors"
            >
              鹿茸
            </button>

            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">麝香</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/9afa30347fd5146577d3a6863453a5df.png"
                          alt="麝香"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>麝香はジャコウジカの香嚢から得られる貴重な動物性生薬です。独特の強い香りを持ち、気を巡らせ、意識を回復させる作用があります。</p>
                        <p>古くから救命薬として用いられ、「安宮牛黄丸」などの重要な処方に配合されています。微量でも強力な作用を示し、気付け薬や香薬としても有名です。</p>
                        <p>心拍数を安定させ、精神を落ち着かせる働きがあり、ストレス社会の現代においても心身を整える香りの生薬として注目されています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open 麝香 modal:', err);
                }
              }}
              className="bg-indigo-100 text-indigo-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-indigo-200 transition-colors"
            >
              麝香
            </button>

            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">薬用ニンジン</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/d7457bb83774d4073d0a50ed3ae40817.png"
                          alt="薬用ニンジン"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>薬用ニンジン（高麗人参）は、古くから「万能薬」として珍重されてきた代表的な生薬です。</p>
                        <p>疲労回復、体力増強、免疫力向上など幅広い効果があり、「気」を補う作用に優れています。</p>
                        <p>ストレスへの抵抗力を高め、心身のバランスを整える働きがあります。また、血行促進や新陳代謝の活性化にも効果的です。</p>
                        <p>現代では健康維持や病後の回復、更年期障害の改善などに広く活用されています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open 薬用ニンジン modal:', err);
                }
              }}
              className="bg-red-100 text-red-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-red-200 transition-colors"
            >
              薬用ニンジン
            </button>

            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">冬虫夏草</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/5b9246209f3a759dbfabb0f67bff14d8.png"
                          alt="冬虫夏草"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>冬虫夏草は、チベット高原などの高地に生息する非常に希少な生薬です。冬は虫、夏は草という不思議な生態から名付けられました。</p>
                        <p>肺と腎を補う作用があり、呼吸器系の強化や体力増強に優れた効果を発揮します。</p>
                        <p>免疫力を高め、疲労回復を促進し、老化防止にも寄与するとされています。また、咳や喘息などの呼吸器症状の改善にも用いられます。</p>
                        <p>現代では健康食品やサプリメントとしても人気が高く、体質改善や病後の回復に活用されています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open 冬虫夏草 modal:', err);
                }
              }}
              className="bg-cyan-100 text-cyan-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-cyan-200 transition-colors"
            >
              冬虫夏草
            </button>

            <button 
              onClick={() => {
                try {
                  const modal = document.createElement('div');
                  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
                  modal.innerHTML = `
                    <div class="bg-white rounded-2xl p-6 max-w-sm w-full max-h-96 overflow-y-auto">
                      <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-bold text-gray-800">サフラン</h3>
                        <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
                          <i class="ri-close-line text-xl"></i>
                        </button>
                      </div>
                      <div class="mb-4">
                        <img 
                          src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/e3d58b616fd96ff42ac937fec16e6564.png"
                          alt="サフラン"
                          class="w-full h-48 object-contain rounded-xl"
                        />
                      </div>
                      <div class="text-sm text-gray-700 leading-relaxed space-y-3">
                        <p>サフランは、クロッカスの花のめしべを乾燥させた貴重な生薬で、「赤い金」とも呼ばれています。</p>
                        <p>血の巡りを良くし、気分を明るくする作用があり、月経不順や月経痛、女性特有の不調に広く用いられています。</p>
                        <p>また、精神を安定させる働きがあり、不安やうつ症状の緩和にも効果的とされています。抗酸化作用も高く、美容や老化防止にも注目されています。</p>
                        <p>漢方では「番紅花」として知られ、血行促進や鎮静作用を目的に様々な処方に配合されています。</p>
                      </div>
                    </div>
                  `;
                  document.body.appendChild(modal);
                  modal.addEventListener('click', (e) => {
                    if (e.target === modal) modal.remove();
                  });
                } catch (err) {
                  console.error('Failed to open サフラン modal:', err);
                }
              }}
              className="bg-orange-100 text-orange-800 py-3 px-4 rounded-xl text-center text-sm font-medium hover:bg-orange-200 transition-colors"
            >
              サフラン
            </button>
          </div>

          {/* 霊芝モーダル */}
          {showReishiModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-sm w-full max-h-[80vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">霊芝</h3>
                    <button 
                      onClick={() => setShowReishiModal(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <i className="ri-close-line text-gray-600"></i>
                    </button>
                  </div>
                  
                  <div className="mb-4">
                    <img 
                      src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/648ebe73bd6466e6e0e71830986987ad.png"
                      alt="霊芝"
                      className="w-full h-48 object-contain rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-3 text-sm text-gray-600">
                    <p className="font-medium text-gray-800">霊芝（れいし）について</p>
                    <p>霊芝は古くから「仙草」「不老長寿の妙薬」として珍重されてきたキノコです。免疫力を整え、ウイルスや細菌への抵抗力を高めるとともに、ストレスや疲労から体を守ります。抗酸化作用や血圧・血糖の安定化作用もあり、生活習慣病の予防に有用です。副作用が少なく、長期的に体質を整えるのに適しており、現代ではサプリメントや健康食品としても人気があります。心身のバランスを整える自然の恵みです。</p>
                    
                    <p className="text-xs text-gray-500 mt-4">※効果には個人差があります。症状が続く場合は専門医にご相談ください。</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 鹿茸モーダル */}
          {showRokujouModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">鹿茸について</h3>
                  <button 
                    onClick={() => setShowRokujouModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                
                <div className="mb-4">
                  <img 
                    src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/35f65fbb82fabfbd0672bbe348b89615.png"
                    alt="鹿茸"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                </div>
                
                <div className="text-sm text-gray-600 space-y-3">
                  <p>鹿茸は、若い雄ジカのまだ固まっていない角（袋角）を乾燥させた貴重な動物性生薬です。古くから「補陽薬」の代表として知られ、生命力を高め、体を温める作用があります。疲労・冷え・腰や膝の痛み・精力減退・不妊など、体のエネルギー不足（腎陽虚）に用いられます。また、血行を促し、体の成長や回復を助けることから、虚弱体質の改善にも効果的です。滋養強壮薬として高麗人参や亀板と組み合わせて使われること多く、まさに「活力を呼び覚ます生薬」と言える存在です。</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pharmacist Section */}
      <div className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-r from-green-100 to-green-50 rounded-2xl p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-green-200 rounded-full overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/f17de3e03fd1411f1337eebc23e69493.jpeg"
                  alt="代表薬剤師"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h3 className="font-bold text-green-800">代表薬剤師</h3>
                <p className="text-green-700 font-medium">佐藤 勝利</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              「体に優しい薬」「自然に寄り添う治療」を大切に、ひとりひとりの体質や生活に合わせて最適な薬を選び、丁寧に説明いたします。
            </p>
          </div>
        </div>
      </div>

      {/* Trust & Reliability Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            一心堂薬局は信頼される存在でありたい
          </h2>
          <div className="space-y-4">
            <div className="bg-green-50 rounded-2xl p-5 border-l-4 border-green-600">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-shield-check-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 mb-1">強引な販売は行っておりません</h3>
                  <p className="text-sm text-gray-700">お客様のペースに合わせて、必要な分だけご提案いたします</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-2xl p-5 border-l-4 border-green-600">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-chat-smile-2-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 mb-1">相談だけでご購入されなくても大丈夫です。</h3>
                  <p className="text-sm text-gray-700">まずはお気軽にご相談ください。無理にお勧めすることはありません</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-2xl p-5 border-l-4 border-green-600">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-hand-heart-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 mb-1">ご相談は無料です</h3>
                  <p className="text-sm text-gray-700">健康相談や漢方相談は無料でお受けしています</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 rounded-2xl p-5 border-l-4 border-amber-500">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-store-2-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">実店舗も営業しています</h3>
                  <p className="text-sm text-gray-700">長時間の電話相談にはお答えできない場合があります。ご来店でのご相談をお勧めします</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 mb-4">
              安心してご相談いただける環境づくりを心がけています
            </p>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="map-section" className="px-4 py-8 bg-green-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            アクセス・お問い合わせ
          </h2>
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-green-600 text-lg mt-1"></i>
                <div>
                  <p className="text-sm text-gray-600">住所</p>
                  <p className="font-medium text-gray-800">〒289-2241<br />千葉県香取郡多古町多古1038-1</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-phone-line text-green-600 text-lg"></i>
                <div>
                  <p className="text-sm text-gray-600">電話番号</p>
                  <p className="font-medium text-gray-800">0479-70-6065</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-printer-line text-green-600 text-lg"></i>
                <div>
                  <p className="text-sm text-gray-600">FAX</p>
                  <p className="font-medium text-gray-800">0479-70-6064</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-chat-3-line text-green-600 text-lg"></i>
                <div>
                  <p className="text-sm text-gray-600">LINE</p>
                  <p className="font-medium text-gray-800">一心堂薬局公式アカウント</p>
                  <p className="text-xs text-gray-500 mt-1">お気軽にご相談ください</p>
                  <div className="mt-3 flex items-start space-x-3">
                    <div>
                      <img 
                        src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/ac6eec8d3a5bc49072144980003e4867.png"
                        alt="LINE QRコード"
                        className="w-20 h-20 border border-gray-200 rounded-lg"
                      />
                      <p className="text-xs text-gray-500 mt-1">QRコードでLINE追加</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 mb-2">お友達になれる</p>
                      <a 
                        href="https://line.me/R/ti/p/@979cubqn"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                      >
                        <i className="ri-line-fill mr-1"></i>
                        @979cubqn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3229.8!2d140.4756!3d35.7456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.0!3m3!1m2!1s0x6022a0e0e0e0e0e0%3A0x0!2z44CSMjg5LTIyNDEg5Y2D6JGJ55yM6aaZ5Y-W6YOh5aSa5Y-k55S65aSa5Y-kMTAzOC0x!5e0!3m2!1sja!2sjp!4v1640995200000!5m2!1sja!2sjp"
                width="100%"
                height="192"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-xl border border-gray-200"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-green-800 mb-6 text-center">
            お支払方法
          </h2>
          <p className="text-sm text-gray-600 text-center mb-4">
            下記支払方法に対応しています。
          </p>
          <div className="bg-green-50 rounded-2xl p-6">
            <img 
              src="https://static.readdy.ai/image/26ebacc80ad5bf356c036f7614552ca1/1e3f4dcc0845ccc0b8b428cdce00eefe.png"
              alt="お支払方法"
              className="w-full h-auto rounded-xl"
            />
          </div>
        </div>
      </div>

      {/* Customer Reviews Section */}
      <div className="px-4 py-8 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-green-800 mb-2">
              🌸 お客様の声
            </h2>
            <p className="text-sm text-gray-600">
              患者様からのうれしいお言葉
            </p>
          </div>

          <div className="space-y-4">
            {/* Review 1 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%2050s%20Japanese%20man%20cartoon%20illustration%2C%20warm%20expression%2C%20simple%20clean%20style%2C%20soft%20colors%2C%20healthcare%20patient%20character%2C%20round%20face%2C%20gentle%20smile%2C%20manga%20style%20avatar&width=100&height=100&seq=review-man-50s&orientation=squarish"
                    alt="お客様"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="bg-green-50 rounded-2xl rounded-tl-none p-4 relative">
                    <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-green-50 border-r-[12px] border-r-transparent"></div>
                    <p className="text-gray-700 leading-relaxed">
                      漢方薬から処方せんまで説明丁寧です。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%20Japanese%20worker%20in%20work%20uniform%20cartoon%20illustration%2C%20blue%20collar%20worker%2C%20simple%20clean%20style%2C%20soft%20colors%2C%20healthcare%20patient%20character%2C%20construction%20or%20factory%20worker%2C%20gentle%20smile%2C%20manga%20style%20avatar&width=100&height=100&seq=review-worker&orientation=squarish"
                    alt="お客様"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="bg-green-50 rounded-2xl rounded-tl-none p-4 relative">
                    <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-green-50 border-r-[12px] border-r-transparent"></div>
                    <p className="text-gray-700 leading-relaxed">
                      とても親切です。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
                <div className="flex items-start space-x-4">
                <div className="w-14 h-14 flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%2030s%20Japanese%20woman%20cartoon%20illustration%2C%20warm%20expression%2C%20simple%20clean%20style%2C%20soft%20colors%2C%20healthcare%20patient%20character%2C%20young%20adult%20female%2C%20gentle%20smile%2C%20manga%20style%20avatar&width=100&height=100&seq=review-woman-30s&orientation=squarish"
                    alt="お客様"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="bg-green-50 rounded-2xl rounded-tl-none p-4 relative">
                    <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-green-50 border-r-[12px] border-r-transparent"></div>
                    <p className="text-gray-700 leading-relaxed">
                      対応が丁寧で早く、助かりました。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review 4 */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
                <div className="flex items-start space-x-4">
                <div className="w-14 h-14 flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%20Japanese%20businessman%20in%20suit%20cartoon%20illustration%2C%20professional%20office%20worker%2C%20simple%20clean%20style%2C%20soft%20colors%2C%20healthcare%20patient%20character%2C%20young%20adult%20male%2C%20gentle%20smile%2C%20manga%20style%20avatar&width=100&height=100&seq=review-businessman&orientation=squarish"
                    alt="お客様"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="bg-green-50 rounded-2xl rounded-tl-none p-4 relative">
                    <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-t-green-50 border-r-[12px] border-r-transparent"></div>
                    <p className="text-gray-700 leading-relaxed">
                      好酸球性副鼻腔炎で、花粉症の時期は鼻水がつらかったのですが、一心堂さんで処方頂いたサプリを継続していたところ花粉症の症状が治まりました。症状に応じた漢方も処方頂けるので安心です。とても親切で丁寧に優しく色々と説明頂けます。いつもありがとうございます。引き続きよろしくお願いいたします。
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 leading-relaxed">
              皆さまからの温かいお言葉が<br />
              私たちの励みになっています
            </p>
          </div>

          {/* Visitor Counter */}
          <div className="mt-6 text-center">
            <div className="text-xs text-gray-400">
              <div className="font-mono">{visitorCount.toString().padStart(7, '0')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <button className="flex flex-col items-center justify-center py-2 text-green-600">
              <i className="ri-home-5-fill text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button 
              onClick={scrollToMap}
              className="flex flex-col items-center justify-center py-2 text-gray-400"
            >
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-phone-line text-lg"></i>
              <span className="text-xs mt-1">お問合せ</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default HomePage;
